import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import type { InventoryFilters } from '@/lib/api/client/inventory'
import type { SelectionScope } from '@/lib/views/actions'
import type { SelectionRefusal } from '@/lib/warehouse/selection-eligibility'

export interface WarehouseSelection {
  selectedIds: ReadonlySet<string>
  selectedCount: number
  scope: SelectionScope<InventoryFilters> | null
  isSelected: (productId: string) => boolean
  toggle: (productId: string, range?: readonly string[]) => void
  togglePage: (productIds: readonly string[]) => void
  selectAllMatching: (filter: InventoryFilters, expectedTotal: number) => void
  /** Every eligible piece of the filter, with the ones its actions refuse counted apart. */
  selectEligible: (ids: readonly string[], refused: SelectionRefusal) => void
  /** Set only by `selectEligible`: what the filter holds that cannot be selected, and why. */
  refused: SelectionRefusal | null
  /** The selection is the whole current filter, not pieces picked one by one. */
  allMatching: boolean
  clear: () => void
}

/** Selection belongs to the current filter, while page navigation only changes its view. */
export function useWarehouseSelection(filterKey: string): WarehouseSelection {
  const [selectedIds, setSelectedIds] = useState<ReadonlySet<string>>(() => new Set<string>())
  const [matchingScope, setMatchingScope] = useState<SelectionScope<InventoryFilters> | null>(null)
  const [refused, setRefused] = useState<SelectionRefusal | null>(null)
  const [eligibleMatching, setEligibleMatching] = useState(false)
  // The shift-range anchor is the last clicked row, whether that click selected or deselected it.
  const anchorId = useRef<string | null>(null)

  useEffect(() => {
    anchorId.current = null
    setSelectedIds(new Set<string>())
    setMatchingScope(null)
    setRefused(null)
    setEligibleMatching(false)
  }, [filterKey])

  const toggle = useCallback((productId: string, range?: readonly string[]) => {
    const anchor = anchorId.current
    anchorId.current = productId
    setMatchingScope(null)
    setRefused(null)
    setEligibleMatching(false)
    setSelectedIds((current) => {
      const next = new Set(current)
      if (range !== undefined) {
        const anchorIndex = anchor === null ? -1 : range.indexOf(anchor)
        const targetIndex = range.indexOf(productId)
        if (anchorIndex >= 0 && targetIndex >= 0) {
          const [start, end] = anchorIndex <= targetIndex
            ? [anchorIndex, targetIndex]
            : [targetIndex, anchorIndex]
          for (const id of range.slice(start, end + 1)) next.add(id)
          return next
        }
      }
      if (next.has(productId)) next.delete(productId)
      else next.add(productId)
      return next
    })
  }, [])

  const togglePage = useCallback((productIds: readonly string[]) => {
    setMatchingScope(null)
    setRefused(null)
    setEligibleMatching(false)
    setSelectedIds((current) => {
      const next = new Set(current)
      const allSelected = productIds.length > 0 && productIds.every((productId) => next.has(productId))
      productIds.forEach((productId) => {
        if (allSelected) next.delete(productId)
        else next.add(productId)
      })
      return next
    })
  }, [])

  const selectAllMatching = useCallback((filter: InventoryFilters, expectedTotal: number) => {
    anchorId.current = null
    setSelectedIds(new Set<string>())
    setRefused(null)
    setEligibleMatching(false)
    setMatchingScope({ kind: 'filter', filter, expectedTotal })
  }, [])

  const selectEligible = useCallback((ids: readonly string[], refusal: SelectionRefusal) => {
    anchorId.current = null
    setMatchingScope(null)
    setSelectedIds(new Set(ids))
    setRefused(refusal)
    setEligibleMatching(true)
  }, [])

  const clear = useCallback(() => {
    anchorId.current = null
    setSelectedIds(new Set<string>())
    setMatchingScope(null)
    setRefused(null)
    setEligibleMatching(false)
  }, [])
  const isSelected = useCallback(
    (productId: string) => matchingScope?.kind === 'filter' || selectedIds.has(productId),
    [matchingScope, selectedIds],
  )
  const scope = useMemo<SelectionScope<InventoryFilters> | null>(() => {
    if (matchingScope !== null) return matchingScope
    if (selectedIds.size === 0) return null
    return { kind: 'ids', ids: [...selectedIds] }
  }, [matchingScope, selectedIds])

  return {
    selectedIds,
    selectedCount: matchingScope?.kind === 'filter' ? matchingScope.expectedTotal : selectedIds.size,
    scope,
    isSelected,
    toggle,
    togglePage,
    selectAllMatching,
    selectEligible,
    refused,
    allMatching: matchingScope?.kind === 'filter' || (eligibleMatching && selectedIds.size > 0),
    clear,
  }
}
